// SEO optimization utilities

export const seoUtils = {
  /**
   * Generate structured data for the page
   * @param {Object} options - Configuration options
   * @returns {string} - JSON-LD structured data
   */
  generateStructuredData(options = {}) {
    const defaultData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": "Wheel Spinner - Random Name Picker",
      "description": "Free online wheel spinner tool to randomly pick a name from a list.",
      "applicationCategory": "UtilityApplication",
      "operatingSystem": "All",
      "offers": {
        "@type": "Offer",
        "price": "0.00",
        "priceCurrency": "USD"
      }
    };
    
    return JSON.stringify({
      ...defaultData,
      ...options
    });
  },
  
  /**
   * Generate meta tags for the page
   * @param {Object} options - Meta tag options
   * @returns {Object} - Object with meta tag properties
   */
  generateMetaTags(options = {}) {
    const defaults = {
      title: 'Wheel Spinner - Random Name Picker',
      description: 'Free online wheel spinner tool to randomly pick a name from a list.',
      url: 'https://spinwheelnames.com',
      image: '/images/og-image.png'
    };
    
    const data = { ...defaults, ...options };
    
    return {
      title: data.title,
      meta: [
        { name: 'description', content: data.description },
        { property: 'og:title', content: data.title },
        { property: 'og:description', content: data.description },
        { property: 'og:url', content: data.url },
        { property: 'og:image', content: data.image },
        { property: 'og:type', content: 'website' },
        { name: 'twitter:card', content: 'summary_large_image' },
        { name: 'twitter:title', content: data.title },
        { name: 'twitter:description', content: data.description },
        { name: 'twitter:image', content: data.image }
      ]
    };
  },
  
  /**
   * Generate dynamic SEO data for saved wheels with improved validation
   * @param {Object} wheelData - Wheel data object
   * @returns {Object} - SEO configuration object
   */
  generateWheelSEO(wheelData) {
    const wheelName = wheelData.title || 'Spin Wheel';
    const wheelItems = wheelData.names || [];
    const itemCount = wheelItems.length;
    const views = wheelData.views || 0;
    
    // Create engaging descriptions based on wheel content
    let description;
    if (itemCount === 0) {
      description = `Spin the "${wheelName}" wheel. Free online decision maker and random picker tool.`;
    } else if (itemCount <= 3) {
      description = `Spin the "${wheelName}" wheel to choose from: ${wheelItems.join(', ')}. Free online decision maker tool.`;
    } else {
      const preview = wheelItems.slice(0, 3).join(', ');
      description = `Spin the "${wheelName}" wheel with ${itemCount} options including ${preview} and more. Free random picker and decision maker tool.`;
    }
    
    // Ensure description is under 160 characters for SEO
    description = this.truncateText(description, 159);
    
    // Generate relevant keywords
    const keywords = [
      'spin wheel',
      'random picker',
      'decision maker',
      'wheel of fortune',
      wheelName.toLowerCase(),
      ...wheelItems.slice(0, 8).map(item => item.toLowerCase())
    ].join(', ');
    
    const title = `${wheelName} - Interactive Spin Wheel | Free Decision Maker`;
    const canonicalUrl = this.validateCanonicalUrl(`https://spinwheelnames.com/wheel/${wheelData.id}`);
    
    // Generate and validate structured data
    const structuredData = {
      "@context": "https://schema.org",
      "@type": "WebApplication",
      "name": wheelName,
      "description": description,
      "url": canonicalUrl,
      "applicationCategory": "Game",
      "operatingSystem": "Any",
      "offers": {
        "@type": "Offer",
        "price": "0",
        "priceCurrency": "USD"
      },
      "aggregateRating": views > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": "4.5",
        "ratingCount": Math.max(views, 1)
      } : undefined,
      "interactionStatistic": {
        "@type": "InteractionCounter",
        "interactionType": "https://schema.org/ViewAction",
        "userInteractionCount": views
      }
    };
    
    // Validate structured data
    if (!this.validateStructuredData(structuredData)) {
      console.warn('Generated invalid structured data for wheel:', wheelData.id);
    }
    
    return {
      title,
      description,
      keywords,
      canonicalUrl,
      structuredData
    };
  },
  
  /**
   * Truncate text while preserving word boundaries
   * @param {string} text - Text to truncate
   * @param {number} maxLength - Maximum length
   * @returns {string} - Truncated text
   */
  truncateText(text, maxLength) {
    if (text.length <= maxLength) return text;
    
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > 0) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  },
  
  /**
   * Generate canonical URL for any page with improved consistency
   * @param {string} path - The page path
   * @param {Object} params - Additional parameters like language
   * @returns {string} - Full canonical URL
   */
  generateCanonicalUrl(path, params = {}) {
    const baseUrl = 'https://spinwheelnames.com';
    
    // Ensure path starts with /
    const cleanPath = path.startsWith('/') ? path : `/${path}`;
    
    // Remove trailing slash except for root
    const finalPath = cleanPath === '/' ? '/' : cleanPath.replace(/\/$/, '');
    
    // Handle language parameters
    if (params.lang && params.lang !== 'en') {
      return `${baseUrl}${finalPath}?lang=${params.lang}`;
    }
    
    return `${baseUrl}${finalPath}`;
  },

  /**
   * Validate and normalize canonical URLs
   * @param {string} url - URL to validate
   * @returns {string} - Normalized canonical URL
   */
  validateCanonicalUrl(url) {
    // Ensure URL follows consistent format
    if (!url.startsWith('https://spinwheelnames.com')) {
      // If it's a relative path, make it absolute
      if (url.startsWith('/')) {
        return `https://spinwheelnames.com${url}`;
      }
      return `https://spinwheelnames.com/${url}`;
    }
    
    // Remove trailing slash except for root
    if (url !== 'https://spinwheelnames.com/' && url.endsWith('/')) {
      return url.slice(0, -1);
    }
    
    return url;
  },
  
  /**
   * Generate default SEO data for any page with improved validation
   * @param {Object} options - SEO options
   * @returns {Object} - Complete SEO configuration
   */
  generatePageSEO(options = {}) {
    const defaults = {
      title: 'Free Random Name Picker Wheel | SpinWheelNames',
      description: 'Free online random name picker wheel for classrooms, contests, and decision making. No login required - just add names and spin!',
      keywords: 'random name picker, wheel spinner, name picker wheel, classroom tool, contest winner',
      path: '/',
      lang: 'en'
    };
    
    const config = { ...defaults, ...options };
    const canonicalUrl = this.validateCanonicalUrl(
      this.generateCanonicalUrl(config.path, { lang: config.lang })
    );
    
    return {
      title: this.truncateText(config.title, 58),
      description: this.truncateText(config.description, 159),
      keywords: config.keywords,
      canonicalUrl,
      ogImage: 'https://spinwheelnames.com/images/og-image.png'
    };
  },

  /**
   * Validate structured data to ensure consistency
   * @param {Object} data - Structured data object
   * @returns {boolean} - Whether the data is valid
   */
  validateStructuredData(data) {
    // Ensure required fields are present
    const required = ['@context', '@type', 'name', 'description'];
    const missing = required.filter(field => !data[field]);
    
    if (missing.length > 0) {
      console.warn('Missing structured data fields:', missing);
      return false;
    }
    
    return true;
  }
};